import { datadogRum } from '@datadog/browser-rum';
import { useMemo } from 'react';

type LogKind = 'log' | 'warning' | 'error';
type LogCtx = Record<string, unknown>;

export function createDataDogLogger(localDefaultContext?: LogCtx) {
  const merge = (ctx?: LogCtx) =>
    localDefaultContext || ctx
      ? { ...(localDefaultContext ?? {}), ...(ctx ?? {}) }
      : undefined;

  const isInitialized = () => datadogRum.getInitConfiguration() !== undefined;

  const guard = <T extends (...args: any[]) => unknown>(
    methodName: string,
    fn: T,
  ): T => {
    return ((...args: Parameters<T>) => {
      if (!isInitialized()) {
        return;
      }
      return fn(...args);
    }) as T;
  };

  const emit = (kind: LogKind, message: string | Error, ctx?: LogCtx) => {
    const data = { level: kind, ...merge(ctx) };

    if (!isInitialized()) {
      console.warn(
        `DataDog RUM not initialized. Failed to log: %c${message}%c`,
        'background:#b91c1c;color:#fff;font-weight:bold;padding:2px 6px;border-radius:4px;',
        '',
        data,
      );
      return;
    }

    const name = typeof message === 'string' ? message : message.message;

    if (kind === 'error') {
      const err = message instanceof Error ? message : new Error(message);
      datadogRum.addError(err, data);
    } else {
      datadogRum.addAction(name, data);
    }
  };

  return {
    log: (message: string, ctx?: LogCtx) => emit('log', message, ctx),
    warning: (message: string, ctx?: LogCtx) => emit('warning', message, ctx),
    error: (err: string | Error, ctx?: LogCtx) => emit('error', err, ctx),

    setUser: guard(
      'setUser',
      (user: { id?: string; name?: string; email?: string } & LogCtx = {}) =>
        datadogRum.setUser(user as any),
    ),
    addGlobal: guard('addGlobal', (key: string, value: unknown) =>
      datadogRum.setGlobalContextProperty(key, value as any),
    ),
    replaceGlobal: guard('replaceGlobal', (ctx: LogCtx) =>
      datadogRum.setGlobalContext(ctx as any),
    ),
    addTiming: guard('addTiming', (name: string) => datadogRum.addTiming(name)),
    stopSession: guard('stopSession', () => datadogRum.stopSession()),
  };
}

export function useDataDogLogger(localDefaultContext?: LogCtx) {
  return useMemo(
    () => createDataDogLogger(localDefaultContext),
    [localDefaultContext],
  );
}

/**
 * The DataDog logger object returned by createDataDogLogger / useDataDogLogger.
 * Shared so consumers don't redeclare their own narrow `Logger` shapes.
 */
export type DataDogLogger = ReturnType<typeof createDataDogLogger>;
